import axios from 'axios';

const API_BASE_URL = 'https://heavensfruit-api.up.railway.app/api';
const CACHE_VERSION = '1.6'; // Incrementar versión de caché para los nuevos filtros

// Limpiar caché antiguo si es necesario
const initializeCache = () => {
    const storedVersion = localStorage.getItem('cacheVersion');
    if (storedVersion !== CACHE_VERSION) {
        console.log('Nueva versión de caché detectada, limpiando caché antiguo...');
        // Agregar limpieza de caché de actividades
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('activities_') || key === 'activity_stats') {
                localStorage.removeItem(key);
                localStorage.removeItem(`${key}_timestamp`);
            }
        });
        // Limpiar todo el caché relacionado
        localStorage.removeItem('accessToken');
        localStorage.removeItem('products');
        localStorage.removeItem('productsTimestamp');
        localStorage.removeItem('userOrders');
        localStorage.removeItem('adminOrders');
        localStorage.removeItem('ordersTimestamp');
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('userPermissionsTimestamp');
        // Establecer nueva versión
        localStorage.setItem('cacheVersion', CACHE_VERSION);
    }
};

// Ejecutar limpieza al iniciar
initializeCache();

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    withCredentials: false // Cambiado a false para evitar problemas CORS
});

let refreshPromise = null;
const isPublicAuth = url => /\/(?:auth\/(?:login|register|verify-email|resend-verification|logout)|token(?:\/refresh)?)\/?(?:\?|$)/.test(url || '');

// Interceptor para agregar el token a todas las peticiones
apiClient.interceptors.request.use(config => {
    const token = localStorage.getItem('accessToken');
    if (token && !isPublicAuth(config.url)) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Get the CSRF token from cookies if available
    const csrfToken = document.cookie.split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
    
    if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
    }
    
    // Evitar caché en las solicitudes a gallery-images con un enfoque más sencillo
    if (config.url && config.url.includes('gallery-images')) {
        // Añadir timestamp para evitar caché (método más simple)
        const separator = config.url.includes('?') ? '&' : '?';
        config.url = `${config.url}${separator}_nocache=${Date.now()}`;
    }
    
    return config;
});

apiClient.interceptors.response.use(
    response => response,
    async error => {
        const original = error.config;
        if (error.response?.status === 401 && original && !isPublicAuth(original.url) && !original._retry) {
            original._retry = true;
            const refresh = localStorage.getItem('refreshToken');
            if (refresh) {
                try {
                    // Share a rotation between simultaneous requests in this tab.
                    if (!refreshPromise) {
                        refreshPromise = axios.post(`${API_BASE_URL}/token/refresh/`, { refresh })
                            .then(({ data }) => {
                                // Logout or another login while refreshing must not restore an old session.
                                if (localStorage.getItem('refreshToken') !== refresh) {
                                    throw new Error('La sesión cambió durante la renovación');
                                }
                                localStorage.setItem('accessToken', data.access);
                                localStorage.setItem('refreshToken', data.refresh);
                                return data.access;
                            })
                            .finally(() => { refreshPromise = null; });
                    }
                    const access = await refreshPromise;
                    original.headers.Authorization = `Bearer ${access}`;
                    return apiClient(original);
                } catch (refreshError) {
                    // A temporary network error or rate limit must not log the user out.
                    if (![400, 401, 403].includes(refreshError.response?.status)) {
                        return Promise.reject(refreshError);
                    }
                    if (localStorage.getItem('refreshToken') !== refresh) {
                        return Promise.reject(refreshError);
                    }
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                }
            }
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('auth:unauthorized'));
            }
        }
        return Promise.reject(error);
    }
);

export default apiClient;
